import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
} from '@mui/material'
import {
  PushPin as PushPinIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material'
import { Announcement } from '../../services/announcementService'

interface AnnouncementDetailModalProps {
  open: boolean
  announcement: Announcement | null
  onClose: () => void
  onMarkAsRead: (announcementId: string) => void
}

export const AnnouncementDetailModal: React.FC<AnnouncementDetailModalProps> = ({
  open,
  announcement,
  onClose,
  onMarkAsRead,
}) => {
  const { t } = useTranslation('announcement')
  const markedAsReadRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (
      open &&
      announcement &&
      !announcement.isRead &&
      !markedAsReadRef.current.has(announcement.id)
    ) {
      markedAsReadRef.current.add(announcement.id)
      onMarkAsRead(announcement.id)
    }
  }, [open, announcement, onMarkAsRead])

  if (!announcement) {
    return null
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {announcement.isPinned && <PushPinIcon fontSize="small" color="primary" />}
          {announcement.priority === 'HIGH' && <WarningIcon fontSize="small" color="error" />}
          <Typography variant="h6" component="span">
            {announcement.title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          {(announcement.priority === 'HIGH' || announcement.isPinned) && (
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              {announcement.priority === 'HIGH' && (
                <Chip label={t(`priority.${announcement.priority}`)} size="small" color="error" />
              )}
              {announcement.isPinned && (
                <Chip
                  icon={<PushPinIcon />}
                  label={t('dashboard.pinned')}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {t('detail.publishedAt')}: {formatDate(announcement.publishedAt)}
            </Typography>
          </Box>

          {announcement.expiresAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('detail.expiresAt')}: {formatDate(announcement.expiresAt)}
            </Typography>
          )}
        </Box>

        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            lineHeight: 1.8,
          }}
        >
          {announcement.content}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('detail.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
